import type { ReactNode } from 'react';
import { ALERT_LEVEL_LABELS, type AlertLevel } from '@shared/index';
import { FLEET_STATUS_LABELS, type FleetStatus } from '../lib/droneStatus';

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';

const TONES: Record<BadgeTone, string> = {
  neutral: 'border-line bg-surface-3 text-fg-muted',
  accent: 'border-accent/40 bg-accent-soft text-accent',
  ok: 'border-ok/40 bg-ok-soft text-ok',
  info: 'border-info/40 bg-info-soft text-info',
  caution: 'border-caution/50 bg-caution-soft text-caution',
  warning: 'border-warning/50 bg-warning-soft text-warning',
  critical: 'border-critical/50 bg-critical-soft text-critical',
};

/*
 * 实心底色的前景色统一用 `text-fg-inverted`：
 * 浅色主题下深色底配白字，深色/实时主题下亮色底配近黑字。
 * 固定的 `text-white` 在深色主题里会掉到 2.8:1（例如亮红 bg-critical），
 * 达不到 WCAG AA 的 4.5:1，告警色尤其不能这样用。
 */
const SOLID: Record<BadgeTone, string> = {
  neutral: 'border-transparent bg-fg-muted text-surface-0',
  accent: 'border-transparent bg-accent text-accent-fg',
  ok: 'border-transparent bg-ok text-fg-inverted',
  info: 'border-transparent bg-info text-fg-inverted',
  caution: 'border-transparent bg-caution text-fg-inverted',
  warning: 'border-transparent bg-warning text-fg-inverted',
  critical: 'border-transparent bg-critical text-fg-inverted',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  /** 用实心底色强调，用于告警列表等需要一眼看到的场景 */
  solid?: boolean;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', children, solid = false, className = '', title }: BadgeProps) {
  return (
    <span
      title={title}
      className={[
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-4 font-medium whitespace-nowrap',
        solid ? SOLID[tone] : TONES[tone],
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<FleetStatus, BadgeTone> = {
  flying: 'ok',
  idle: 'info',
  offline: 'neutral',
  fault: 'critical',
  disabled: 'neutral',
};

export function FleetStatusBadge({ status, className }: { status: FleetStatus; className?: string }) {
  return (
    <Badge tone={STATUS_TONE[status]} solid={status === 'fault'} className={className}>
      <span
        aria-hidden
        className={['size-1.5 rounded-full bg-current', status === 'flying' ? 'animate-pulse' : ''].join(' ')}
      />
      {FLEET_STATUS_LABELS[status]}
    </Badge>
  );
}

export const ALERT_LEVEL_TONES: Record<AlertLevel, BadgeTone> = {
  nominal: 'ok',
  caution: 'caution',
  warning: 'warning',
  critical: 'critical',
};

export function AlertLevelBadge({ level, children }: { level: AlertLevel; children?: ReactNode }) {
  return (
    <Badge tone={ALERT_LEVEL_TONES[level]} solid={level === 'critical'}>
      {children ?? ALERT_LEVEL_LABELS[level]}
    </Badge>
  );
}

/** 信号/电量等质量指示条，颜色随严重程度变化。 */
export function QualityBar({
  value,
  min = 0,
  max = 100,
  tone = 'ok',
  label,
  ariaLabel,
}: {
  value: number;
  min?: number;
  max?: number;
  tone?: BadgeTone;
  label?: string;
  /** 无障碍名称；用于进度条旁已单独显示数值、不需要再渲染 `label` 的场景 */
  ariaLabel?: string;
}) {
  const ratio = max === min ? 0 : Math.min(1, Math.max(0, (value - min) / (max - min)));
  return (
    <div className="flex items-center gap-2" title={label}>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
        role="progressbar"
        aria-label={ariaLabel ?? label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
      >
        <div
          className={['h-full rounded-full', SOLID[tone]].join(' ')}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      {label ? <span className="tabular shrink-0 text-[11px] text-fg-muted">{label}</span> : null}
    </div>
  );
}
